import React from 'react';
import { sectionStyles, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import useScrollReveal from '../useScrollReveal';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';


export default function AppMockupSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'phone';
    const device = config.device || 'phone';
    const imageUrl = content.image_url || '';
    const features = content.features || [];

    const PhoneMockup = ({ children, className = '' }) => (
        <div className={`inline-block ${className}`}>
            <div className="rounded-[2.5rem] border-[4px] border-gray-800 bg-gray-900 shadow-2xl overflow-hidden" style={{ width: 280 }}>
                <div className="flex justify-center pt-2 pb-1 bg-gray-900"><div className="w-20 h-4 bg-gray-800 rounded-full" /></div>
                <div className="bg-white overflow-hidden" style={{ height: 560 }}>
                    {children}
                </div>
                <div className="flex justify-center py-2 bg-gray-900"><div className="w-24 h-1 bg-gray-700 rounded-full" /></div>
            </div>
        </div>
    );

    const LaptopMockup = ({ children, className = '' }) => (
        <div className={`inline-block ${className}`}>
            <div className="rounded-t-xl border-[3px] border-gray-700 bg-gray-800 overflow-hidden shadow-2xl" style={{ maxWidth: 640 }}>
                <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-800">
                    <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-400" /><div className="w-2.5 h-2.5 rounded-full bg-yellow-400" /><div className="w-2.5 h-2.5 rounded-full bg-green-400" /></div>
                    <div className="flex-1 mx-8"><div className="h-5 bg-gray-700 rounded-md" /></div>
                </div>
                <div className="bg-white overflow-hidden" style={{ aspectRatio: '16/10' }}>
                    {children}
                </div>
            </div>
            <div className="mx-auto h-4 rounded-b-lg bg-gradient-to-b from-gray-600 to-gray-500" style={{ width: '110%', maxWidth: 700, marginLeft: '-5%' }} />
        </div>
    );

    const IPhoneMockup = ({ children, className = '' }) => (
        <div className={`inline-block ${className}`}>
            <div className="relative rounded-[44px] border-[3px] border-gray-800 bg-black shadow-2xl overflow-hidden" style={{ width: 280 }}>
                {/* Dynamic Island */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 w-[90px] h-[28px] bg-black rounded-full" />
                <div className="bg-white overflow-hidden rounded-[41px]" style={{ height: 580 }}>
                    {children}
                </div>
                {/* Home indicator */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-28 h-1 bg-gray-600 rounded-full" />
            </div>
        </div>
    );

    // ── Laptop variant ──
    if (variant === 'laptop') {
        const laptopImage = imageUrl ? <img src={imageUrl} alt={content.title || ''} className="w-full h-full object-cover object-top" loading="lazy" decoding="async" /> : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50">
                <span className="text-gray-300 text-sm">Vista previa</span>
            </div>
        );
        return (
            <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} position="top" color={s.accent} />
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="mt-8 flex justify-center">
                        <div className="inline-block w-full max-w-[720px]">
                            {/* Laptop screen */}
                            <div className="rounded-t-2xl border-[3px] border-gray-700 bg-gray-800 overflow-hidden shadow-2xl">
                                {/* Top bar with camera */}
                                <div className="flex items-center justify-center py-2 bg-gray-800 relative">
                                    <div className="w-2 h-2 rounded-full bg-gray-600 absolute" />
                                </div>
                                {/* Screen content */}
                                <div className="bg-white overflow-hidden" style={{ aspectRatio: '16/10' }}>
                                    {laptopImage}
                                </div>
                            </div>
                            {/* Keyboard / base */}
                            <div className="relative mx-auto">
                                <div className="h-3 bg-gradient-to-b from-gray-600 to-gray-500 rounded-b-lg" style={{ width: '104%', marginLeft: '-2%' }} />
                                <div className="h-1.5 bg-gray-400 rounded-b-xl mx-auto" style={{ width: '40%' }} />
                            </div>
                        </div>
                    </div>
                    {content.cta_text && (
                        <a {...resolveLinkProps(content.cta_link || '#')} className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-xl text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all" style={{ backgroundColor: primaryColor }}>
                            {content.cta_text}
                        </a>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
            </section>
        );
    }

    const MockupWrapper = variant === 'iphone' ? IPhoneMockup : device === 'laptop' ? LaptopMockup : PhoneMockup;
    const mockupImage = imageUrl ? <img src={imageUrl} alt={content.title || ''} className="w-full h-full object-cover object-top" loading="lazy" decoding="async" /> : (
        <div className="w-full h-full flex items-center justify-center bg-gray-50">
            <span className="text-gray-300 text-sm">Vista previa de la app</span>
        </div>
    );

    // Split variant — mockup + content side by side
    if (variant === 'split') {
        const reversed = config.reversed;
        return (
            <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} position="top" color={s.accent} />

                <div ref={ref} className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className={`grid md:grid-cols-2 gap-8 lg:gap-16 items-center ${reversed ? 'md:[direction:rtl]' : ''}`}>
                        <div className={`flex justify-center ${reversed ? 'md:[direction:ltr]' : ''}`}>
                            <MockupWrapper>{mockupImage}</MockupWrapper>
                        </div>
                        <div className={reversed ? 'md:[direction:ltr]' : ''}>
                            <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} align="left" />
                            {content.body && <p className={`mt-4 text-base leading-relaxed ${s.dark ? 'text-white/70' : 'text-gray-600'}`}>{content.body}</p>}
                            {features.length > 0 && (
                                <div className="mt-6 space-y-3">
                                    {features.map((f, i) => (
                                        <div key={i} className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-white text-sm font-bold" style={{ backgroundColor: primaryColor }}>
                                                {f.icon || '✓'}
                                            </div>
                                            <div>
                                                <p className={`text-sm font-semibold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{f.title}</p>
                                                {f.description && <p className={`text-xs mt-0.5 ${s.dark ? 'text-white/50' : 'text-gray-500'}`}>{f.description}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {content.cta_text && (
                                <a {...resolveLinkProps(content.cta_link || '#')} className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all" style={{ backgroundColor: primaryColor }}>
                                    {content.cta_text}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
            </section>
        );
    }

    // Floating variant — mockup floats over gradient bg
    if (variant === 'floating') {
        return (
            <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} position="top" color={s.accent} />

                <div className="absolute inset-0 opacity-30" style={{ background: `radial-gradient(circle at 50% 80%, ${hexToRgba(primaryColor, 0.4)}, transparent 60%)` }} />
                <div ref={ref} className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="mt-8 flex justify-center">
                        <div className="transform hover:-translate-y-2 transition-transform duration-500">
                            <MockupWrapper>{mockupImage}</MockupWrapper>
                        </div>
                    </div>
                    {content.cta_text && (
                        <a {...resolveLinkProps(content.cta_link || '#')} className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-xl text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all" style={{ backgroundColor: primaryColor }}>
                            {content.cta_text}
                        </a>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
            </section>
        );
    }

    // Default: phone centered
    return (
        <section className={`${s.padClass} relative overflow-hidden ${s.radiusClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} position="top" color={s.accent} />
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="mt-8 flex justify-center">
                    <MockupWrapper>{mockupImage}</MockupWrapper>
                </div>
                {content.cta_text && (
                    <a {...resolveLinkProps(content.cta_link || '#')} className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-xl text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all" style={{ backgroundColor: primaryColor }}>
                        {content.cta_text}
                    </a>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} position="bottom" color={s.accent} />
        </section>
    );
}
